import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import {
    PiMagnifyingGlass,
    PiEnvelopeSimple,
    PiEyeSlash,
    PiClock,
    PiShieldCheck,
    PiChartLineUp,
    PiHandshake,
    PiUsers,
    PiRobot
} from 'react-icons/pi';

import '../styles/Landing.css';

function Landing() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
        const unsub = onAuthStateChanged(auth, (user) => {
            if (user) navigate('/dashboard', { replace: true });
        });
        return () => unsub();
    }, [navigate]);

    return (
        <div className="lp-root">

            {/* 1. Rich Navbar */}
            <nav className="lp-container">
                <div className="lp-nav">
                    <Link to="/" className="lp-nav-brand">
                        UpConvert
                        <span className="lp-nav-tagline">AI Sales Summaries</span>
                    </Link>
                    <div className="lp-nav-links">
                        <a href="#problem">The Problem</a>
                        <a href="#how">How it works</a>
                        <a href="#pricing">Pricing</a>
                    </div>
                    <div className="lp-nav-actions">
                        <Link to="/login" className="lp-btn-login">Log in</Link>
                        <Link to="/signup" className="lp-btn-cta">Start your free trial</Link>
                    </div>
                </div>
            </nav>

            {/* 2. Hero Section */}
            <header className="lp-hero-section lp-container">
                <div className="lp-hero-grid">
                    <div className="lp-hero-content">
                        <span className="lp-hero-eyebrow">For SaaS & Agency Teams</span>
                        <h1>Turn sales calls into closing notes with AI.</h1>
                        <p>UpConvert securely listens to your Google Meet and Zoom calls to write the exact follow-up notes you need. Stop typing. Start closing.</p>
                        <div className="lp-hero-actions">
                            <Link to="/signup" className="lp-btn-primary">Start your free trial</Link>
                            <a href="#how" className="lp-btn-secondary">See how it works</a>
                        </div>
                    </div>

                    {/* CSS Only Mockup - The Dashboard */}
                    <div className="lp-mockup-wrapper">
                        <div className="lp-mockup-header">
                            <div className="lp-mockup-dot"></div>
                            <div className="lp-mockup-dot"></div>
                            <div className="lp-mockup-dot"></div>
                        </div>
                        <div className="lp-mockup-body split">
                            {/* Left Pane: Live Call Transcription */}
                            <div className="lp-live-call">
                                <div className="lp-call-header">
                                    <div className="lp-recording-indicator">
                                        <div className="pulse-ring"></div>
                                        <span>Recording</span>
                                    </div>
                                    <span className="lp-call-time">14:02</span>
                                </div>
                                <div className="lp-transcript">
                                    <div className="anim-prospect">
                                        <p className="lp-speaker"><strong>Prospect</strong> <span className="timestamp">13:58</span></p>
                                        <p className="lp-spoken-text">"So we need a way to automate our follow-ups for a team of 5 reps. We're spending way too much time manually logging CRM details."</p>
                                    </div>

                                    <div className="anim-you">
                                        <p className="lp-speaker"><strong>You</strong> <span className="timestamp">14:01</span></p>
                                        <p className="lp-spoken-text typing">"Got it. Our automated CRM sync sounds perfect for that..."</p>
                                    </div>
                                </div>
                            </div>

                            {/* Right Pane: AI Summary Extraction */}
                            <div className="lp-ai-summary">
                                <div className="lp-summary-header">
                                    <div className="anim-robot"><PiRobot size={20} /></div>
                                    <span className="anim-status-text"></span>
                                </div>
                                <div className="lp-summary-content">
                                    <div className="anim-pop-1">
                                        <div className="lp-summary-badge">Need Identified</div>
                                        <p className="lp-extracted-text">Seeking automated follow-ups & CRM sync for 5 reps.</p>
                                    </div>

                                    <div className="anim-pop-2">
                                        <div className="lp-summary-badge action">Action Item</div>
                                        <div className="lp-mockup-line"></div>
                                        <div className="lp-mockup-line short"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* 3. The Problem Section */}
            <section id="problem" className="lp-problems-section">
                <div className="lp-container">
                    <h2 className="lp-section-title">Deals are not lost on price.</h2>
                    <p className="lp-section-sub">They are lost in the details. For modern sales teams, managing notes manually is costing you business.</p>

                    <div className="lp-problems-grid">
                        <div className="lp-problem-card">
                            <div className="lp-problem-icon"><PiMagnifyingGlass /></div>
                            <h3>Lost Info</h3>
                            <p>Good notes get buried or forgotten in notebooks or docs.</p>
                        </div>
                        <div className="lp-problem-card">
                            <div className="lp-problem-icon"><PiEnvelopeSimple /></div>
                            <h3>Bad Follow-ups</h3>
                            <p>Sending a generic email template kills response rates.</p>
                        </div>
                        <div className="lp-problem-card">
                            <div className="lp-problem-icon"><PiEyeSlash /></div>
                            <h3>Blind Spots</h3>
                            <p>Managers cannot see why a deal slipped without digging through tape.</p>
                        </div>
                        <div className="lp-problem-card">
                            <div className="lp-problem-icon"><PiClock /></div>
                            <h3>Time Sink</h3>
                            <p>You spend hours every week re-watching recordings just to type an email.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4. The Solution Section */}
            <section className="lp-solution-section lp-container">
                <div className="lp-solution-grid">

                    {/* CSS Only Mockup - AI Note */}
                    <div className="lp-note-mockup">
                        <div className="lp-note-header">
                            <div className="lp-note-avatar"><PiRobot color="var(--lp-accent)" /></div>
                            <div>
                                <h4 style={{ margin: '0 0 4px', fontSize: 16 }}>AI Summary Generated</h4>
                                <span style={{ fontSize: 13, color: 'var(--lp-text-light)' }}>Just now</span>
                            </div>
                        </div>
                        <span className="lp-note-badge">Action Items</span>
                        <div className="lp-note-block">
                            <div className="lp-mockup-line" style={{ height: 6, marginBottom: 8 }}></div>
                            <div className="lp-mockup-line short" style={{ height: 6 }}></div>
                        </div>
                        <span className="lp-note-badge">Draft Email</span>
                        <div className="lp-note-block" style={{ background: 'var(--lp-accent-light)' }}>
                            <div className="lp-mockup-line" style={{ height: 6, marginBottom: 8, background: '#fff' }}></div>
                            <div className="lp-mockup-line" style={{ height: 6, marginBottom: 8, background: '#fff' }}></div>
                            <div className="lp-mockup-line short" style={{ height: 6, background: '#fff' }}></div>
                        </div>
                    </div>

                    <div className="lp-solution-content">
                        <h2>UpConvert finds what actually matters.</h2>
                        <div className="lp-solution-list">
                            <div className="lp-solution-item">
                                <h4><div className="lp-solution-dot">1</div> Fast Notes</h4>
                                <p>Discover what matters most to your prospect within seconds, without rewatching the recording.</p>
                            </div>
                            <div className="lp-solution-item">
                                <h4><div className="lp-solution-dot">2</div> Smart Quotes</h4>
                                <p>Jump straight to the exact moment the lead asked about price or features.</p>
                            </div>
                            <div className="lp-solution-item">
                                <h4><div className="lp-solution-dot">3</div> Written Emails</h4>
                                <p>We draft a personalized follow-up email based on what they actually care about.</p>
                            </div>
                        </div>

                        {/* Privacy & Security Block */}
                        <div className="lp-privacy-block">
                            <h4><PiShieldCheck color="var(--lp-accent)" size={20} /> Enterprise-Grade Privacy</h4>
                            <p>Your call data is completely private. Transcripts are encrypted securely and never used to train third-party AI models.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 5. Full Width Banner */}
            <section className="lp-impact-banner">
                <div className="lp-container lp-impact-grid">
                    <div>
                        <h3>Save Time</h3>
                        <p>No more typing up notes after a long call.</p>
                    </div>
                    <div>
                        <h3>Stronger Emails</h3>
                        <p>Send follow-ups that sound smart and accurate.</p>
                    </div>
                    <div>
                        <h3>More Deals</h3>
                        <p>Close faster by never dropping the ball again.</p>
                    </div>
                </div>
            </section>

            {/* 6. How it Works */}
            <section id="how" className="lp-steps-section">
                <div className="lp-container">
                    <h2 className="lp-section-title">Three steps to better deals</h2>
                    <p className="lp-section-sub">Designed for speed. Available for Chrome and Edge.</p>

                    <div className="lp-steps-grid">
                        <div className="lp-step">
                            <div className="lp-step-num">1</div>
                            <h4>Install Extension</h4>
                            <p>Setup takes less than 2 minutes.</p>
                        </div>
                        <div className="lp-step">
                            <div className="lp-step-num">2</div>
                            <h4>Run Calls Normally</h4>
                            <p>Works seamlessly on Google Meet and Zoom.</p>
                        </div>
                        <div className="lp-step">
                            <div className="lp-step-num">3</div>
                            <h4>Get Instant Notes</h4>
                            <p>Receive complete summaries instantly in the browser popup.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 7. Built For Trust Bar */}
            <section className="lp-trust-bar">
                <div className="lp-container">
                    <p className="lp-trust-label">Built For</p>
                    <div className="lp-trust-icons">
                        <div className="lp-trust-item"><PiChartLineUp /> SaaS Sales</div>
                        <div className="lp-trust-item"><PiHandshake /> Agencies</div>
                        <div className="lp-trust-item"><PiUsers /> 3-6 Rep Teams</div>
                    </div>
                </div>
            </section>

            {/* 8. Pricing Section */}
            <section id="pricing" className="lp-pricing-section lp-container">
                <h2 className="lp-section-title">Simple, transparent pricing</h2>
                <p className="lp-section-sub" style={{ marginBottom: 48 }}>Find the plan that fits your team's size. No hidden fees.</p>

                <div className="lp-pricing-grid">
                    {/* Growth Plan */}
                    <div className="lp-pricing-card">
                        <h3>Growth</h3>
                        <p className="lp-pricing-desc">For small teams building their sales engine.</p>
                        <div className="lp-price">$149 <span>/ mo</span></div>
                        <span className="lp-trial-note">Start your 30-day free trial, then $149/month.</span>

                        <ul className="lp-pricing-features">
                            <li>Up to 5 reps</li>
                            <li>No-bot meeting recordings</li>
                            <li>AI follow-up emails</li>
                            <li>Priority chat support</li>
                        </ul>
                        <Link to="/signup" className="lp-btn-pricing ghost">Start your free trial</Link>
                    </div>

                    {/* Scale Plan */}
                    <div className="lp-pricing-card popular">
                        <div className="lp-pricing-badge">Best Value</div>
                        <h3>Scale</h3>
                        <p className="lp-pricing-desc">For growing agencies and sales floors.</p>
                        <div className="lp-price">$249 <span>/ mo</span></div>
                        <span className="lp-trial-note">Start your 30-day free trial, then $249/month.</span>

                        <ul className="lp-pricing-features">
                            <li>Up to 10 reps</li>
                            <li>Everything in Growth</li>
                            <li>Priority support & feature requests</li>
                        </ul>
                        <Link to="/signup" className="lp-btn-pricing">Start your free trial</Link>
                    </div>
                </div>
            </section>

            {/* 9. Final CTA Box */}
            <section className="lp-cta-section">
                <div className="lp-cta-box">
                    <h2>You already have the leads. Let us help you close them.</h2>
                    <p>Join the successful teams using UpConvert to turn lost business into closed deals.</p>
                    <Link to="/signup" className="lp-btn-primary">Start your free trial</Link>
                </div>
            </section>

            {/* 10. Footer */}
            <footer className="lp-footer lp-container">
                <div className="lp-footer-brand">UpConvert</div>
                <p className="lp-footer-copy">© {new Date().getFullYear()} UpConvert. All rights reserved.</p>
                <div className="lp-footer-links">
                    <a href="/privacy-policy.html">Privacy Policy</a>
                    <a href="/terms-of-service.html">Terms of Service</a>
                    <a href="mailto:mkane@upconvertapp.com">Contact</a>
                </div>
            </footer>
        </div>
    );
}

export default Landing;
